import { useEffect, useMemo, useRef, useState } from 'react';
import { systemAnnouncements, type ChatModeForAnnouncements, type SystemAnnouncement } from '../data/systemAnnouncements';

export type AnnouncementSlot = {
  slotKey: string;
  afterMessageIndex: number;
  announcement: SystemAnnouncement;
  shownAt: number;
};

type UseSystemAnnouncementsArgs = {
  assistantResponseCount: number;
  latestAssistantMessageIndex: number | null;
  mode: ChatModeForAnnouncements;
  threadKey?: string | null;
  suppressed: boolean;
  hasReadableAnswer: boolean;
  isListeningMode: boolean;
  isQuizAvailable: boolean;
};

const CADENCE_COUNTS = [2, 7, 15];
const MIN_RESPONSE_GAP = 4;
const MAX_CARDS_PER_MOUNT = 3;
const SOFT_DELAY_MS = 1200;

export const useSystemAnnouncements = ({
  assistantResponseCount,
  latestAssistantMessageIndex,
  mode,
  threadKey,
  suppressed,
  hasReadableAnswer,
  isListeningMode,
  isQuizAvailable,
}: UseSystemAnnouncementsArgs) => {
  const [slots, setSlots] = useState<AnnouncementSlot[]>([]);
  const dismissedIdsRef = useRef(new Set<string>());
  const shownIdsRef = useRef(new Set<string>());
  const shownCountRef = useRef(0);
  const lastShownResponseCountRef = useRef(0);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    setSlots([]);
    lastShownResponseCountRef.current = 0;
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [threadKey]);

  const nextAnnouncement = useMemo(() => {
    if (suppressed || latestAssistantMessageIndex === null) return null;
    if (shownCountRef.current >= MAX_CARDS_PER_MOUNT) return null;
    const nextCadenceCount = CADENCE_COUNTS.find((count) => count > lastShownResponseCountRef.current);
    if (!nextCadenceCount || assistantResponseCount < nextCadenceCount) return null;
    if (assistantResponseCount - lastShownResponseCountRef.current < MIN_RESPONSE_GAP && lastShownResponseCountRef.current !== 0) {
      return null;
    }

    return systemAnnouncements
      .filter((announcement) => announcement.modes.includes(mode))
      .filter((announcement) => assistantResponseCount >= announcement.minResponseCount)
      .filter((announcement) => !announcement.requiresReadableAnswer || hasReadableAnswer)
      .filter((announcement) => !announcement.requiresQuizAvailable || isQuizAvailable)
      .filter((announcement) => !announcement.hideWhenListening || !isListeningMode)
      .filter((announcement) => !dismissedIdsRef.current.has(announcement.id))
      .filter((announcement) => !shownIdsRef.current.has(announcement.id))
      .sort((a, b) => b.priority - a.priority)[0] ?? null;
  }, [assistantResponseCount, hasReadableAnswer, isListeningMode, isQuizAvailable, latestAssistantMessageIndex, mode, suppressed]);

  useEffect(() => {
    if (!nextAnnouncement || latestAssistantMessageIndex === null || suppressed) return;
    if (timerRef.current) window.clearTimeout(timerRef.current);

    timerRef.current = window.setTimeout(() => {
      const slotKey = `${threadKey || 'thread'}:${latestAssistantMessageIndex}:${nextAnnouncement.id}`;
      shownIdsRef.current.add(nextAnnouncement.id);
      shownCountRef.current += 1;
      lastShownResponseCountRef.current = assistantResponseCount;
      setSlots((current) => {
        if (current.some((slot) => slot.slotKey === slotKey)) return current;
        return [
          ...current,
          {
            slotKey,
            afterMessageIndex: latestAssistantMessageIndex,
            announcement: nextAnnouncement,
            shownAt: Date.now(),
          },
        ];
      });
      timerRef.current = null;
    }, SOFT_DELAY_MS);

    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [assistantResponseCount, latestAssistantMessageIndex, nextAnnouncement, suppressed, threadKey]);

  const dismissAnnouncementSlot = (slotKey: string) => {
    setSlots((current) => {
      const target = current.find((slot) => slot.slotKey === slotKey);
      if (target) dismissedIdsRef.current.add(target.announcement.id);
      return current.filter((slot) => slot.slotKey !== slotKey);
    });
  };

  const markAnnouncementActed = (slotKey: string) => {
    setSlots((current) => {
      const target = current.find((slot) => slot.slotKey === slotKey);
      if (target) dismissedIdsRef.current.add(target.announcement.id);
      return current.filter((slot) => slot.slotKey !== slotKey);
    });
  };

  return {
    slots,
    dismissAnnouncementSlot,
    markAnnouncementActed,
  };
};
